import { useState, useRef } from "react";
import type { HackerProfile } from "@/hooks/useHackers";
import { getSupabase } from "@/lib/supabase";
import { trackEvent } from "@/lib/analytics";

interface HireRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  hacker: HackerProfile | null;
}

const projectTypes = [
  "Penetration Testing",
  "Database Hacking",
  "Social Media Account Recovery",
  "Mobile Device Access",
  "Network Intrusion",
  "Malware Development",
  "Cryptocurrency Tracing",
  "Website Take Down",
  "Email Account Access",
  "Corporate Espionage",
  "Social Engineering",
  "Darknet Operations",
  "Forensic Analysis",
  "Bug Bounty",
  "Other",
];

const budgetRanges = [
  "Under $500",
  "$500 - $1,000",
  "$1,000 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
];

const timelines = [
  "24 hours or less",
  "2-3 days",
  "1 week",
  "2-3 weeks",
  "1 month",
  "Negotiable",
];

export default function HireRequestModal({ isOpen, onClose, hacker }: HireRequestModalProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  if (!isOpen || !hacker) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("success");
      if (formRef.current) formRef.current.reset();
      setTimeout(() => {
        setStatus("idle");
        onClose();
      }, 2000);
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9d39k2lh2lp4rjvt1tg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      if (response.ok && parsed?.code === "OK") {
        // Fire GA4 conversion: hire_expert
        trackEvent("hire_expert", {
          hacker_name: hacker.name,
          hacker_specialty: hacker.specialty,
          project_type: (formData.get("project_type") as string) || "",
          budget_range: (formData.get("budget_range") as string) || "",
        });

        setStatus("success");
        if (formRef.current) formRef.current.reset();

        // Fire email notification to concierge via Supabase edge function (non-blocking)
        try {
          const supabase = getSupabase();
          supabase.functions.invoke("hire-request-notify", {
            body: Object.fromEntries(formData as unknown as Map<string, string>),
          });
        } catch {
          // Silently fail — form data is still captured
        }

        setTimeout(() => {
          setStatus("idle");
          onClose();
        }, 2500);
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#161b22] rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#1c2128] text-gray-400 transition-colors z-10"
        >
          <i className="fas fa-times" />
        </button>

        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-200">Request to Hire a Hacker</h2>
            <p className="text-sm text-gray-500 mt-1">
              Your request will be reviewed by our concierge team who will match you with the best hacker for your needs.
            </p>
          </div>

          {status === "success" && (
            <div className="mb-5 p-4 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
              <i className="fas fa-check-circle mr-2" />
              Request submitted! Our concierge team will review your request and get back to you within 24 hours.
            </div>
          )}

          {status === "error" && (
            <div className="mb-5 p-4 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
              <i className="fas fa-exclamation-circle mr-2" />
              {errorMsg}
            </div>
          )}

          <form
            data-readdy-form
            ref={formRef}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="absolute opacity-0 pointer-events-none"
            />

            <input type="hidden" name="requested_hacker" value={hacker.name} />
            <input type="hidden" name="hacker_specialty" value={hacker.specialty} />
            <input type="hidden" name="hacker_rate" value={hacker.rate} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Your Name *</label>
                <input
                  type="text"
                  name="client_name"
                  required
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                />
              </div>
            </div>

            <div className="bg-[#1c2128] rounded p-3 border border-gray-800">
              <p className="text-xs text-gray-500 mb-1">Requested Hacker</p>
              <p className="text-sm font-semibold text-gray-200">{hacker.name} — <span className="text-[#5bc0de]">{hacker.specialty}</span></p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">Project Type *</label>
              <select
                name="project_type"
                required
                className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
              >
                <option value="">Select project type...</option>
                {projectTypes.map((pt) => (
                  <option key={pt} value={pt}>{pt}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Budget Range *</label>
                <select
                  name="budget_range"
                  required
                  className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                >
                  <option value="">Select budget...</option>
                  {budgetRanges.map((br) => (
                    <option key={br} value={br}>{br}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Timeline *</label>
                <select
                  name="timeline"
                  required
                  className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                >
                  <option value="">Select timeline...</option>
                  {timelines.map((tl) => (
                    <option key={tl} value={tl}>{tl}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">Job Description *</label>
              <textarea
                name="job_description"
                required
                rows={4}
                maxLength={500}
                placeholder="Describe your project in detail. What do you need done? Any specific requirements or constraints?"
                className="w-full px-3 py-2.5 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de] resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
            </div>

            <div className="pt-2 flex gap-3">
              <button
                type="submit"
                disabled={status === "loading"}
                className="flex-1 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap"
              >
                {status === "loading" ? (
                  <span><i className="fas fa-spinner fa-spin mr-2" />Submitting...</span>
                ) : (
                  <span><i className="fas fa-paper-plane mr-2" />Submit Request</span>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-600 text-gray-400 text-sm font-semibold rounded hover:bg-[#1c2128] transition-all whitespace-nowrap"
              >
                Cancel
              </button>
            </div>

            <p className="text-xs text-gray-400 text-center">
              Your request goes directly to our concierge team, not to the hacker. We will review and match you appropriately.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}